"use client";

import type { RecipeBox, RecipeBoxMember, RecipeBoxRole, Recipe } from "@/lib/types";

/**
 * Get the user's role in a recipe box
 */
export function getUserRoleInBox(
  box: RecipeBox | null | undefined,
  userId: string | null | undefined,
): RecipeBoxRole | null {
  if (!box || !userId) {
    return null;
  }

  // Check if user is the original owner
  if (box.ownerUid === userId) {
    return "owner";
  }

  // Check if user is in members array
  const member = box.members?.find((m) => m.uid === userId);
  return member?.role ?? null;
}

/**
 * Check if user can view recipes in a recipe box
 * All roles (owner, member, viewer) can view
 */
export function canViewRecipes(
  box: RecipeBox | null | undefined,
  userId: string | null | undefined,
): boolean {
  const role = getUserRoleInBox(box, userId);
  return role !== null; // Any role can view
}

/**
 * Check if user can add recipes to a recipe box
 * Owner and member can add, viewer cannot
 */
export function canAddRecipes(
  box: RecipeBox | null | undefined,
  userId: string | null | undefined,
): boolean {
  const role = getUserRoleInBox(box, userId);
  return role === "owner" || role === "member";
}

/**
 * Check if user can edit a specific recipe
 * Owner can edit any recipe, member can only edit their own recipes, viewer cannot edit
 */
export function canEditRecipe(
  box: RecipeBox | null | undefined,
  recipe: Recipe | null | undefined,
  userId: string | null | undefined,
): boolean {
  if (!recipe || !userId) {
    return false;
  }

  const role = getUserRoleInBox(box, userId);
  
  if (role === "owner") {
    return true; // Owner can edit any recipe
  }
  
  if (role === "member") {
    return recipe.ownerUid === userId; // Member can only edit their own recipes
  }
  
  return false; // Viewer cannot edit
}

/**
 * Check if user can delete a recipe
 * Only owner can delete recipes, members and viewers cannot
 */
export function canDeleteRecipe(
  box: RecipeBox | null | undefined,
  userId: string | null | undefined,
): boolean {
  const role = getUserRoleInBox(box, userId);
  return role === "owner";
}

/**
 * Check if user can manage members (invite, change roles, remove)
 * Only owner can manage members
 */
export function canManageMembers(
  box: RecipeBox | null | undefined,
  userId: string | null | undefined,
): boolean {
  const role = getUserRoleInBox(box, userId);
  return role === "owner";
}

/**
 * Check if user can change a member's role
 * Only owner can change roles, and cannot remove other owners
 */
export function canChangeMemberRole(
  box: RecipeBox | null | undefined,
  targetMember: RecipeBoxMember | null | undefined,
  userId: string | null | undefined,
): boolean {
  if (!canManageMembers(box, userId)) {
    return false;
  }

  // Cannot change role of another owner
  if (targetMember?.role === "owner") {
    return false;
  }

  return true;
}

/**
 * Check if user can remove a member
 * Only owner can remove members, and cannot remove other owners
 */
export function canRemoveMember(
  box: RecipeBox | null | undefined,
  targetMember: RecipeBoxMember | null | undefined,
  userId: string | null | undefined,
): boolean {
  if (!canManageMembers(box, userId)) {
    return false;
  }

  // Cannot remove another owner
  if (targetMember?.role === "owner") {
    return false;
  }

  // Cannot remove yourself
  if (targetMember?.uid === userId) {
    return false;
  }

  return true;
}

/**
 * Check if user can export recipes
 * Only owner can export, member and viewer cannot
 */
export function canExportRecipes(
  box: RecipeBox | null | undefined,
  userId: string | null | undefined,
): boolean {
  const role = getUserRoleInBox(box, userId);
  return role === "owner";
}

